import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { BaseComponent } from '../../../../../shared/base-components/base.component';
import { DocumentItemKeyType } from '../../../../../shared/enums/document-item-key-type.enum';
import { DocumentItemKeyData } from '../../../../../shared/models/document/document-itemkey.model';
import { DocumentSearchCriteria } from '../../../../../shared/models/document/document-search-criteria.model';
import { ValidationModel } from '../../../../../shared/models/validation-results/validation.model';
import { ControlValidationMessagesService } from '../../../../../shared/validators/control-validation-messages.service';
import { IVehicleOwnershipDeInsuranceReasonType, VehicleOwnershipDeInsuranceReasonType } from '../../../../models/enums/vehicle-ownership-de-insurance-reason-type.enum';
import { VehicleOwnershipDeInsuranceModel } from '../../../../models/vehicle-ownership-de-insurance.model';
import { VehicleOwnershipDeInsuranceService } from '../../../../services/vehicle-ownership-de-insurance.service';

export class VehicleOwnershipDeInsuranceModalData {
    constructor(public vehicleOwnershipDeInsurance: VehicleOwnershipDeInsuranceModel, public title: string, public buttonText: string, public shouldSave: boolean, public customerNumberId: number = null) {}
}

@Component({
    selector: 'vehicle-ownership-de-insurance-modal',
    templateUrl: './vehicle-ownership-de-insurance-modal.html'
})

export class VehicleOwnershipDeInsuranceModalComponent extends BaseComponent {
    public dataLoaded = false;
    documentCategoryCollection: any[];
    public documentItemKeyCollection: DocumentItemKeyData[];
    public validationModel: ValidationModel = new ValidationModel([]);
    public addDeInsuranceForm: UntypedFormGroup;
    public reasonTypes: IVehicleOwnershipDeInsuranceReasonType[];
    public searchDocumentDataCollection: DocumentSearchCriteria[];

    constructor(public fb: UntypedFormBuilder,
        public dialogRef: MatDialogRef<VehicleOwnershipDeInsuranceModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: VehicleOwnershipDeInsuranceModalData,
        public vehicleOwnershipDeInsuranceService: VehicleOwnershipDeInsuranceService) {
        super();

        if (this.data.vehicleOwnershipDeInsurance.effectiveDate) {
            this.data.vehicleOwnershipDeInsurance.effectiveDate = moment(this.data.vehicleOwnershipDeInsurance.effectiveDate).toDate();
            if (this.data.vehicleOwnershipDeInsurance.effectiveDate.getFullYear() === 0 || (this.data.vehicleOwnershipDeInsurance.effectiveDate.getDate() === 1 && this.data.vehicleOwnershipDeInsurance.effectiveDate.getMonth() === 0 && this.data.vehicleOwnershipDeInsurance.effectiveDate.getFullYear() === 1)) {
                this.data.vehicleOwnershipDeInsurance.effectiveDate = null;
            }
        } else {
            this.data.vehicleOwnershipDeInsurance.effectiveDate = null;
        }
    }

    // Initialize the class
    ngOnInit() {
        this.dataLoaded = true;
        this.reasonTypes = new VehicleOwnershipDeInsuranceReasonType().GetAll();
        this.setControls();
        this.getSearchCriteria();
        this.setDocumentItemKeyCollection();
    }

    private getSearchCriteria() {
        this.searchDocumentDataCollection = [];
        const documentSearchCriteria1 = new DocumentSearchCriteria();
        documentSearchCriteria1.documentItemKeyTypeId = DocumentItemKeyType.VehicleOwnership;
        documentSearchCriteria1.itemKeyId = this.data.vehicleOwnershipDeInsurance.vehicleOwnershipId;
        this.searchDocumentDataCollection.push(documentSearchCriteria1);

        if (this.data.customerNumberId > 0) {
            const documentSearchCriteria2 = new DocumentSearchCriteria();
            documentSearchCriteria2.documentItemKeyTypeId = DocumentItemKeyType.Customer;
            documentSearchCriteria2.itemKeyId = this.data.customerNumberId;
            this.searchDocumentDataCollection.push(documentSearchCriteria2);
        }
    }

    setControls() {
        this.addDeInsuranceForm = new UntypedFormGroup({
            effectiveDate: new UntypedFormControl({ value: this.data.vehicleOwnershipDeInsurance.effectiveDate ? moment(this.data.vehicleOwnershipDeInsurance.effectiveDate).format('L') : null, disabled: false }, ControlValidationMessagesService.minDateRequired),
            endDate: new UntypedFormControl({ value: this.data.vehicleOwnershipDeInsurance.endDate ? moment(this.data.vehicleOwnershipDeInsurance.endDate).format('L') : null, disabled: false }, Validators.compose([])),
            vehicleOwnershipDeInsuranceReasonTypeId: new UntypedFormControl({ value: this.data.vehicleOwnershipDeInsurance.vehicleOwnershipDeInsuranceReasonTypeId, disabled: false }, Validators.compose([]))
        });
    }

    updateView() {
        let effectiveDate = this.data.vehicleOwnershipDeInsurance.effectiveDate ? moment(this.data.vehicleOwnershipDeInsurance.effectiveDate).format('L') : null;
        this.addDeInsuranceForm.get('effectiveDate').setValue(effectiveDate);
        let endDate = this.data.vehicleOwnershipDeInsurance.endDate ? moment(this.data.vehicleOwnershipDeInsurance.endDate).format('L') : null;
        this.addDeInsuranceForm.get('endDate').setValue(endDate);
        this.addDeInsuranceForm.get('vehicleOwnershipDeInsuranceReasonTypeId').setValue(this.data.vehicleOwnershipDeInsurance.vehicleOwnershipDeInsuranceReasonTypeId);
    }

    markAsTouched() {
        (<UntypedFormControl> this.addDeInsuranceForm.controls['effectiveDate']).markAsTouched();
    }

    cancel() {
        this.dialogRef.close();
    }

    save() {
        this.markAsTouched();
        if (this.addDeInsuranceForm.valid) {
            this.data.vehicleOwnershipDeInsurance.effectiveDate = this.addDeInsuranceForm.get('effectiveDate').value ? moment(this.addDeInsuranceForm.get('effectiveDate').value).toDate() : null;
            this.data.vehicleOwnershipDeInsurance.endDate = this.addDeInsuranceForm.get('endDate').value ? moment(this.addDeInsuranceForm.get('endDate').value).toDate() : null;
            this.data.vehicleOwnershipDeInsurance.vehicleOwnershipDeInsuranceReasonTypeId = this.addDeInsuranceForm.get('vehicleOwnershipDeInsuranceReasonTypeId').value ? this.addDeInsuranceForm.get('vehicleOwnershipDeInsuranceReasonTypeId').value : 0;
            if (this.data.shouldSave) {
                this.vehicleOwnershipDeInsuranceService.saveVehicleOwnershipDeInsurance(this.data.vehicleOwnershipDeInsurance).subscribe(r => {
                    this.dialogRef.close(this.data.vehicleOwnershipDeInsurance);
                },
                err => {
                    this.validationModel = new ValidationModel(err);
                });
            } else {
                this.dialogRef.close(this.data.vehicleOwnershipDeInsurance);
            }
        }
    }

    /**
     * Checks if documentItemKeyCollection is null and if searchDocumentDataCollection is not null. If true then the documentItemKeyCollection will
     * be populated with the searchDocumentDataCollection data
     */
    private setDocumentItemKeyCollection() {

        if (!this.documentItemKeyCollection || this.documentItemKeyCollection.length == 0 && this.searchDocumentDataCollection && this.searchDocumentDataCollection.length > 0) {

            this.documentItemKeyCollection = new Array<DocumentItemKeyData>();

            for (var i = 0; i < this.searchDocumentDataCollection.length; i++) {
                if (this.searchDocumentDataCollection[i].itemKeyId && this.searchDocumentDataCollection[i].documentItemKeyTypeId) {
                    var itemKeyData = new DocumentItemKeyData();
                    itemKeyData.documentItemKeyTypeId = this.searchDocumentDataCollection[i].documentItemKeyTypeId;
                    itemKeyData.itemKeyId = this.searchDocumentDataCollection[i].itemKeyId;
                    this.documentItemKeyCollection.push(itemKeyData);
                }
                if (this.searchDocumentDataCollection[i].documentCategoryTypeId) {
                    this.documentCategoryCollection.push(this.searchDocumentDataCollection[i].documentCategoryTypeId);
                }
            }
        }
    }
}
